/**
 * EditableDetailGridCard - DetailGridCard powered by FormFieldMeta.
 *
 * Extends the existing DetailGridCard with:
 * 1. Automatic display formatting based on field_type (date, currency, etc.)
 * 2. Optional edit button per section that opens an EditFormDialog
 * 3. Consistent grid item rendering
 *
 * Supports both single-section and multi-section modes, matching
 * the original DetailGridCard API.
 *
 * Usage (single section):
 *   <EditableDetailGridCard
 *     title="Product Details"
 *     icon={<Package size={16} />}
 *     items={[
 *       { field: defineField({ code_name: 'sku', display_name: 'SKU', field_type: 'TEXT' }), value: product.sku },
 *       { field: defineField({ code_name: 'price', display_name: 'Price', field_type: 'MONEY' }), value: product.price },
 *     ]}
 *     editConfig={{
 *       onSave: async (values) => { await mutation.mutateAsync(values) },
 *       isPending: mutation.isPending,
 *       canEdit: can('products.edit'),
 *     }}
 *     formatOptions={{ locale, currency }}
 *   />
 *
 * Usage (multi-section):
 *   <EditableDetailGridCard
 *     sections={[
 *       {
 *         title: "General",
 *         icon: <Info size={16} />,
 *         items: generalItems,
 *         editConfig: generalEditConfig,
 *       },
 *       {
 *         title: "Warranty",
 *         icon: <Shield size={16} />,
 *         items: warrantyItems,
 *         editConfig: warrantyEditConfig,
 *       },
 *     ]}
 *     formatOptions={{ locale, currency }}
 *   />
 */

import { type ReactNode, useState, useMemo } from 'react'
import { Separator } from '@/shared/components/ui/separator'
import { cn } from '@/shared/lib/utils'
import { formatDisplayValue, isDisplayEmpty } from '@/shared/lib/forms/display'
import type { DisplayFormatOptions } from '@/shared/lib/forms/display'
import { useFormattingContext } from '@/shared/lib/formatting'
import { EditFormDialog } from './edit-form-dialog'
import { EditIconButton } from './edit-icon-button'
import { normaliseDetailCardDisplayValue } from './detail-card-formatting'
import type { CardFieldItem, EditConfig } from './editable-card-types'

// ── Types ──────────────────────────────────────────────

export interface EditableGridSection {
  title: string
  icon: ReactNode
  items: CardFieldItem[]
  /** Per-section edit config. Omit for read-only sections. */
  editConfig?: EditConfig
  /** Per-section action (rendered alongside edit button). */
  action?: ReactNode
  emptyState?: ReactNode
  className?: string
  headerClassName?: string
  gridClassName?: string
}

interface EditableDetailGridCardProps {
  /** Single-section mode. */
  title?: string
  icon?: ReactNode
  items?: CardFieldItem[]
  editConfig?: EditConfig
  action?: ReactNode
  emptyState?: ReactNode
  /** Multi-section mode. */
  sections?: EditableGridSection[]
  /** Locale/currency for display formatting. */
  formatOptions?: DisplayFormatOptions
  className?: string
  headerClassName?: string
  gridClassName?: string
}

// ── Main component ─────────────────────────────────────

export function EditableDetailGridCard({
  title,
  icon,
  items,
  editConfig,
  action,
  emptyState,
  sections,
  formatOptions,
  className,
  headerClassName,
  gridClassName,
}: EditableDetailGridCardProps) {
  const orgFormatting = useFormattingContext()
  const effectiveFormatOptions: DisplayFormatOptions = {
    locale: formatOptions?.locale ?? orgFormatting.locale,
    currency: formatOptions?.currency ?? orgFormatting.currency,
  }
  const resolved: EditableGridSection[] = sections ??
    (title && icon
      ? [{
          title,
          icon,
          items: items ?? [],
          editConfig,
          action,
          emptyState,
          headerClassName,
          gridClassName,
        }]
      : [])

  if (resolved.length === 0) return null

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-[var(--rule)] bg-[var(--bg-light)] shadow-[var(--shadow-card)]',
        className,
      )}
    >
      {resolved.map((sec, idx) => (
        <EditableGridSectionBlock
          key={sec.title}
          section={sec}
          formatOptions={effectiveFormatOptions}
          showSeparator={idx > 0}
        />
      ))}
    </section>
  )
}

// ── Section block ──────────────────────────────────────

function EditableGridSectionBlock({
  section,
  formatOptions,
  showSeparator,
}: {
  section: EditableGridSection
  formatOptions?: DisplayFormatOptions
  showSeparator: boolean
}) {
  const [editOpen, setEditOpen] = useState(false)
  // Snapshot at open - parent rebuilds items each render and would reset mid-edit.
  const [editSnapshot, setEditSnapshot] = useState<{
    fields: CardFieldItem['field'][]
    values: Record<string, unknown>
  }>({ fields: [], values: {} })
  const sec = section

  // Build edit fields/values from non-readOnly items
  const { editFields, editValues } = useMemo(() => {
    const fields = sec.items
      .filter((item) => !item.readOnly && !item.field.disabled)
      .map((item) => item.field)
    const values: Record<string, unknown> = {}
    for (const item of sec.items) {
      if (!item.readOnly && !item.field.disabled) {
        values[item.field.code_name] = item.value ?? ''
      }
    }
    return { editFields: fields, editValues: values }
  }, [sec.items])

  const showEditButton =
    sec.editConfig &&
    sec.editConfig.canEdit !== false &&
    editFields.length > 0

  // Build grid items
  const siblingValues = Object.fromEntries(
    sec.items.map((item) => [item.field.code_name, item.value]),
  )
  const gridItems = sec.items.map((item) => {
    const displayVal = item.displayValue ??
      (item.value && typeof item.value === 'object'
        ? normaliseDetailCardDisplayValue(item.value)
        : formatDisplayValue(item.field, item.value, { ...formatOptions, values: siblingValues }))
    const empty = isDisplayEmpty(item.value) && !item.displayValue

    return {
      key: item.field.code_name,
      label: item.field.display_name,
      value: displayVal,
      empty,
    }
  })

  const hasItems = gridItems.length > 0

  return (
    <>
      {showSeparator && (
        <Separator className="h-px w-full bg-[var(--rule-strong)]" />
      )}
      <div className={cn('p-3 sm:p-5', sec.className)}>
        {/* Section header */}
        <div className={cn('mb-5 flex items-center justify-between gap-3', sec.headerClassName)}>
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-[var(--green-bg)] text-[var(--green)] [&_svg]:h-4 [&_svg]:w-4">
              {sec.icon}
            </span>
            <h2 className="truncate text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {sec.title}
            </h2>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {sec.action}
            {showEditButton && (
              <EditIconButton
                onClick={() => {
                  setEditSnapshot({ fields: editFields, values: editValues })
                  setEditOpen(true)
                }}
              />
            )}
          </div>
        </div>

        {/* Grid */}
        <div
          className={cn(
            'grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3',
            sec.gridClassName,
          )}
        >
          {hasItems ? (
            gridItems.map((item) => (
              <div key={item.key} className="min-w-0">
                <div className="mb-0.5 text-[14px] font-normal text-[var(--ink-2)]">
                  {item.label}
                </div>
                <div
                  className={cn(
                    'break-words leading-[1.5] text-[var(--ink)]',
                    item.empty && 'text-[var(--ink-3)]',
                  )}
                >
                  {item.value}
                </div>
              </div>
            ))
          ) : (
            <div className="sm:col-span-2 xl:col-span-3">
              {sec.emptyState ?? (
                <p className="text-[var(--ink-3)]">No details available.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      {showEditButton && sec.editConfig && (
        <EditFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          title={`Edit ${sec.title}`}
          fields={editSnapshot.fields}
          values={editSnapshot.values}
          onSave={sec.editConfig.onSave}
          isPending={sec.editConfig.isPending}
          requireReason={sec.editConfig.requireReason}
          submissionMode={sec.editConfig.submissionMode}
          onFileUpload={sec.editConfig.onFileUpload}
        />
      )}
    </>
  )
}
